import { useNavigate } from 'react-router-dom';
import { AlertBanner, Button } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useCanManageBilling } from '../hooks/use-can-manage-billing';
import { useCreditBillingPaths } from '../hooks/use-credit-billing-paths';
import { useCreditUsage } from '../hooks/use-credit-usage';
import { creditsBannerView, CreditViewer } from '../utils/credit-usage-view';

/** Full-width strip above the page, like the other account-wide banners. */
const STRIP_CLASS_NAME = 'rounded-none border-x-0 border-t-0';
const TEST_ID = 'credits-exhausted-banner';

/**
 * Notice that AI is paused, the payer's credits spent: its billing admin gets the way to billing,
 * other members are sent to the admin, a master account's client learns whom to contact.
 */
export function CreditsExhaustedBanner() {
  const t = useFeatureTranslation('credits.banner');
  const navigate = useNavigate();
  const canManageBilling = useCanManageBilling();
  const { billing } = useCreditBillingPaths();
  const { creditUsage } = useCreditUsage();

  const view = creditsBannerView(creditUsage, canManageBilling);
  if (!view) return null;

  if (view.viewer === CreditViewer.MANAGED_CLIENT) {
    return (
      <AlertBanner
        variant='warning'
        size='sm'
        role='status'
        testId={TEST_ID}
        className={STRIP_CLASS_NAME}
        title={t('clientTitle', { master: view.masterName })}
      />
    );
  }

  return (
    <AlertBanner
      variant='warning'
      size='sm'
      role='status'
      testId={TEST_ID}
      className={STRIP_CLASS_NAME}
      title={t('payerTitle')}
      body={view.canManageBilling ? undefined : t('askAdmin')}
    >
      {view.canManageBilling && (
        <Button
          variant='default'
          outline='default'
          size='sm'
          testId='credits-exhausted-banner-billing'
          onClick={() => navigate(billing)}
        >
          {t('payerAction')}
        </Button>
      )}
    </AlertBanner>
  );
}
